import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "progress-container"
}
const _hoisted_2 = { class: "progress" }
const _hoisted_3 = ["rows"]

import { ref, computed, watch, reactive } from 'vue';
import { useStore } from 'vuex';

import ApplicationAction, { APPLICATION_ACTION_TYPE } from '../../models/application-action';
import {
  EPINIO_TYPES,
  APPLICATION_SOURCE_TYPE,
  EpinioApplication,
  EpinioAppSource,
  EpinioAppBindings
} from '../../types';
import { makeProgressStateCell } from '../../utils/table-formatters';


export default /*@__PURE__*/_defineComponent({
  __name: 'AppProgress',
  props: {
    application: {},
    source: {},
    bindings: {},
    mode: {},
    tab: {},
    active: { type: Boolean }
  },
  emits: ['finished'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const store = useStore();
const t = store.getters['i18n/t'];

const running = ref(false);
const actions = ref<ApplicationAction[]>([]);

const columns = [
  {
    field: 'name',
    label: t('epinio.applications.steps.progress.table.stage.label'),
    width: '150px',
    sortable: false,
  },
  {
    field: 'description',
    label: t('tableHeaders.description'),
    width: '450px',
    sortable: false,
    formatter: (_v: any, row: any) => {
      const wrapper = document.createElement('span');

      wrapper.style.cssText = 'display:flex; flex-direction:column;';

      const main = document.createElement('span');

      main.textContent = row.description || '';
      wrapper.appendChild(main);

      // stateMessage is set on failure — show it as secondary error text
      if (row.stateMessage) {
        const sub = document.createElement('span');

        sub.style.cssText = 'font-size:0.85em; color:var(--error); margin-top:2px;';
        sub.textContent = row.stateMessage;
        wrapper.appendChild(sub);
      }

      return wrapper;
    },
  },
  {
    field: 'stateDisplay',
    label: t('epinio.applications.steps.progress.table.status'),
    width: '150px',
    sortable: false,
    formatter: (_v: any, row: any) => makeProgressStateCell(row),
  },
];

const actionsToRun = computed(() => actions.value.filter(action => action.run));

// tableRows is a copy of actions that tracks state and stateMessage so any change to those properties triggers a Lit re-render
const tableRows = computed(() => {
  // Track state and stateMessage so any change triggers a Lit re-render
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  actions.value.forEach((a: ApplicationAction) => { a.state; (a as any).stateMessage; }); // touch props to trigger Lit re-render

  return [...actions.value];
});
const fetchApp = async () => {
  try {
    await props.application.forceFetch();
  } catch (err) { // eslint-disable-line @typescript-eslint/no-unused-vars
    // silent catch
  }
};

const create = async () => {
  // Make each action reactive so changes to their state trigger updates in the UI
  actions.value = actions.value.map((a: ApplicationAction) => reactive(a) as ApplicationAction);
  running.value = true;
  const enabledActions = [...actionsToRun.value];

  for (const action of enabledActions) {
    try {
      await action.execute({ source: props.source });
    } catch (err) {
      running.value = false;
      console.error(err);
      const errDetails = err instanceof Error ? err.message : String(err);

      store.dispatch('growl/error', {
        title: props.mode === 'edit'
          ? t('epinio.growl.application.update.error.title')
          : t('epinio.growl.application.deploy.error.title'),
        message: props.mode === 'edit'
          ? t('epinio.growl.application.update.error.message', { name: props.application.meta.name, error: errDetails })
          : t('epinio.growl.application.deploy.error.message', { name: props.application.meta.name, error: errDetails }),
      });
      await fetchApp();
      return;
    }
  }
  
  store.dispatch('growl/success', {
    title: props.mode === 'edit'
      ? t('epinio.growl.application.update.success.title')
      : t('epinio.growl.application.deploy.success.title'),
    message: props.mode === 'edit'
      ? t('epinio.growl.application.update.success.message', { name: props.application.meta.name })
      : t('epinio.growl.application.deploy.success.message', { name: props.application.meta.name }),
  });
  await fetchApp();
  running.value = false;
  emit('finished', true);
};

watch(running, (neu, prev) => {
  if (prev && !neu) {
    props.tab.completed = true;
  }
});

const createActions = async () => {
  const REDEPLOY_SOURCE = props.mode === 'edit';

  const coreArgs = {
    application: props.application,
    bindings: props.bindings,
    type: EPINIO_TYPES.APP_ACTION,
  };

  if (!REDEPLOY_SOURCE) {
    actions.value.push(await store.dispatch('epinio/create', {
      action: APPLICATION_ACTION_TYPE.CREATE,
      index: 1,
      ...coreArgs
    }));
  } else {
    actions.value.push(await store.dispatch('epinio/create', {
      action: APPLICATION_ACTION_TYPE.UPDATE_SOURCE,
      index: 2,
      ...coreArgs
    }));
  }

  if (props.bindings?.configurations?.length && !REDEPLOY_SOURCE) {
    actions.value.push(await store.dispatch('epinio/create', {
      action: APPLICATION_ACTION_TYPE.BIND_CONFIGURATIONS,
      index: 3,
      ...coreArgs
    }));
  }

  if (props.bindings?.services?.length && !REDEPLOY_SOURCE) {
    actions.value.push(await store.dispatch('epinio/create', {
      action: APPLICATION_ACTION_TYPE.BIND_SERVICES,
      index: 4,
      ...coreArgs
    }));
  }

  const srcType = props.source.type;

  if ([APPLICATION_SOURCE_TYPE.ARCHIVE, APPLICATION_SOURCE_TYPE.FOLDER].includes(srcType)) {
    actions.value.push(await store.dispatch('epinio/create', {
      action: APPLICATION_ACTION_TYPE.UPLOAD,
      index: 5,
      ...coreArgs
    }));
  }

  if ([APPLICATION_SOURCE_TYPE.GIT_URL, APPLICATION_SOURCE_TYPE.GIT_HUB, APPLICATION_SOURCE_TYPE.GIT_LAB].includes(srcType)) {
    actions.value.push(await store.dispatch('epinio/create', {
      action: APPLICATION_ACTION_TYPE.GIT_FETCH,
      index: srcType === APPLICATION_SOURCE_TYPE.GIT_URL ? 5 : 6,
      ...coreArgs
    }));
  }

  if ([APPLICATION_SOURCE_TYPE.ARCHIVE, APPLICATION_SOURCE_TYPE.FOLDER, APPLICATION_SOURCE_TYPE.GIT_URL,
       APPLICATION_SOURCE_TYPE.GIT_HUB, APPLICATION_SOURCE_TYPE.GIT_LAB].includes(srcType)) {
    actions.value.push(await store.dispatch('epinio/create', {
      action: APPLICATION_ACTION_TYPE.BUILD,
      index: 7,
      ...coreArgs
    }));
  }

  actions.value.push(await store.dispatch('epinio/create', {
    action: APPLICATION_ACTION_TYPE.DEPLOY,
    index: 8,
    ...coreArgs
  }));

  create();
};

watch(() => props.active, (isActive) => {
  if (isActive && !actions.value.length) {
    createActions();
  }
});

return (_ctx: any,_cache: any) => {
  return (!_ctx.$fetchState?.pending)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("trailhand-table", {
            rows: tableRows.value,
            columns: columns,
            searchable: false,
            "key-field": "key"
          }, null, 8, _hoisted_3)
        ]),
        _cache[0] || (_cache[0] = _createTextVNode()),
        _createElementVNode("h3", null, "The application will continue " + _toDisplayString(props.mode === 'edit' ? 'updating' : 'deploying') + " in the background. Feel free to close this modal.", 1)
      ]))
    : _createCommentVNode("", true)
}
}

})